// /influio-tv — one dark "on-air" section with two tabs. "Yayın" (default) plays the newest
// Episode big; the archive rail on the right groups every Episode by Sales Day (a day can hold
// several: Episode 1, Episode 2 …), the script is clamped with a read-more, and the download
// is the one loud button. "Stüdyo" mounts the studio only while open, so the 3D stage never
// runs behind the player.
import React, { useCallback, useEffect, useState } from 'react';
import {
    DownloadOutlined, ExpandLess, ExpandMore, LiveTvOutlined, VideocamOutlined, VisibilityOffOutlined
} from '@mui/icons-material';
import dayjs from 'dayjs';
import BreadcrumbsBar from '../../layouts/breadcrumb/BreadcrumbsBar';
import { KARACA_OFFER_ID, listEpisodes, withdrawEpisode } from '../../services/influioTvService';
import InfluioTvStudio from './Studio/InfluioTvStudio';
import styles from './InfluioTv.module.scss';

const MONTHS = ['OCA', 'ŞUB', 'MAR', 'NİS', 'MAY', 'HAZ', 'TEM', 'AĞU', 'EYL', 'EKİ', 'KAS', 'ARA'];
const longDate = (d) => new Date(`${d}T12:00:00+03:00`)
    .toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
const relative = (d) => {
    const diff = dayjs().startOf('day').diff(dayjs(d), 'day');
    return diff === 0 ? 'Bugün' : diff === 1 ? 'Dün' : '';
};
const CLAMP_CHARS = 420; // ponytail: a char count stands in for a line count; good enough for a read-more
// the server lists the newest Sales Day first; inside a day, Episode 1 is the first one saved
const groupByDay = (list) => {
    const days = [];
    for (const e of list) {
        let day = days.find((d) => d.date === e.date);
        if (!day) days.push((day = { date: e.date, list: [] }));
        day.list.push(e);
    }
    days.forEach((d) => d.list.sort((a, b) => a.id - b.id));
    return days;
};

const Broadcast = () => {
    const [episodes, setEpisodes] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');

    // signed play URLs live 15 minutes: every load refreshes the whole list, which is
    // one query and a handful of signatures
    const load = useCallback(() => {
        listEpisodes(KARACA_OFFER_ID)
            .then((r) => {
                const list = r.data?.episodes || [];
                setEpisodes(list);
                setSelectedId((prev) => (prev && list.some((e) => e.id === prev) ? prev : list[0]?.id ?? null));
            })
            .catch((e) => setError(e.response?.data?.error || e.message));
    }, []);

    useEffect(() => { load(); }, [load]);

    const days = groupByDay(episodes);
    const selected = episodes.find((e) => e.id === selectedId) || null;
    const dayOf = selected ? days.find((d) => d.date === selected.date) : null;
    const number = dayOf ? dayOf.list.findIndex((e) => e.id === selected.id) + 1 : 0;
    const long = (selected?.transcript?.length || 0) > CLAMP_CHARS;

    const pick = (id) => { setSelectedId(id); setExpanded(false); };

    const withdraw = async () => {
        if (!selected) return;
        if (!window.confirm(`${longDate(selected.date)} · Episode ${number} yayından geri çekilsin mi?`)) return;
        try {
            await withdrawEpisode(selected.id);
            load();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
    };

    // ponytail: the file downloads as whatever it was taped as — mp4 for every new Episode,
    // WebM only for the ones taped before the studio went mp4-only
    const download = async () => {
        if (!selected || downloading) return;
        setDownloading(true);
        try {
            const blob = await fetch(selected.playUrl).then((r) => r.blob());
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `influio-tv-${selected.date}-episode-${number}.${blob.type.includes('webm') ? 'webm' : 'mp4'}`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch {
            window.open(selected.playUrl, '_blank'); // storage said no to CORS — save from the tab
        } finally {
            setDownloading(false);
        }
    };

    if (!selected) {
        return (
            <div className={`${styles.testCard} ${styles.rise} ${styles.d2}`}>
                {error && <div className={styles.error}><span>{error}</span><button onClick={() => setError('')}>✕</button></div>}
                <div className={styles.bars} />
                <div className={styles.noSignal}>
                    <h3>Sinyal yok</h3>
                    <p>Henüz bir Episode yayınlanmadı. Stüdyo sekmesinden ilk bülteni kaydedin.</p>
                </div>
            </div>
        );
    }

    const format = /\.webm/i.test(selected.video_url || '') ? 'WEBM' : 'MP4';
    return (
        <div className={styles.grid}>
            <div>
                {error && <div className={styles.error}><span>{error}</span><button onClick={() => setError('')}>✕</button></div>}
                <div className={`${styles.nowPlaying} ${styles.rise} ${styles.d1}`}>
                    <span><b>● Şimdi oynatılıyor</b> · {longDate(selected.date)}</span>
                    <span>Episode {number} / {dayOf.list.length}</span>
                </div>
                <video key={selected.id} className={`${styles.player} ${styles.rise} ${styles.d2}`} src={selected.playUrl} controls playsInline />
                <div className={`${styles.rise} ${styles.d3}`}>
                    <h2 className={styles.episodeTitle}>{selected.title || 'Günlük bülten'}</h2>
                    <div className={styles.episodeDate}>{longDate(selected.date)} · Episode {number}{dayOf.list.length > 1 ? ` / ${dayOf.list.length}` : ''}</div>
                    <div className={styles.actions}>
                        <button className={styles.download} onClick={download} disabled={downloading}>
                            <DownloadOutlined />
                            {downloading ? 'Hazırlanıyor…' : 'Videoyu indir'}
                            <small>{format}</small>
                        </button>
                        <button className={styles.ghost} onClick={withdraw}>
                            <VisibilityOffOutlined fontSize="small" /> Yayından geri çek
                        </button>
                    </div>
                </div>
                {selected.transcript && (
                    <div className={`${styles.card} ${styles.rise} ${styles.d4}`}>
                        <div className={styles.cardHead}>
                            <span>Script · Lia</span>
                            <span>{selected.transcript.trim().split(/\s+/).length} kelime</span>
                        </div>
                        <div className={`${styles.script} ${long && !expanded ? styles.clamped : ''}`}>{selected.transcript}</div>
                        {long && (
                            <button className={styles.readMore} onClick={() => setExpanded((v) => !v)}>
                                {expanded ? <>Daha az göster <ExpandLess fontSize="small" /></> : <>Devamını oku <ExpandMore fontSize="small" /></>}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <aside className={`${styles.rail} ${styles.rise} ${styles.d3}`}>
                <div className={styles.railHead}>
                    <span>Arşiv · Tüm yayınlar</span>
                    <span>{days.length} gün · {episodes.length} bölüm</span>
                </div>
                {days.map(({ date, list }) => {
                    const d = dayjs(date);
                    const rel = relative(date);
                    return (
                        <section key={date} className={`${styles.dayGroup} ${date === selected.date ? styles.onDay : ''}`}>
                            <div className={styles.dayHead}>
                                <div className={styles.day}>
                                    <strong>{d.date()}</strong>
                                    <span>{MONTHS[d.month()]} {String(d.year()).slice(2)}</span>
                                </div>
                                <div className={styles.dayName}>
                                    <b>{longDate(date)}</b>
                                    <small>{list.length} episode{rel && <> · <em>{rel}</em></>}</small>
                                </div>
                            </div>
                            {list.map((e, i) => (
                                <button key={e.id} className={`${styles.episode} ${e.id === selectedId ? styles.on : ''}`} onClick={() => pick(e.id)}>
                                    <span className={styles.epNo}>{i + 1}</span>
                                    <div className={styles.epMeta}>
                                        <small>Episode {i + 1}</small>
                                        <b>{e.title || 'Günlük bülten'}</b>
                                        {e.transcript && <p>{e.transcript.replace(/\s+/g, ' ').slice(0, 120)}</p>}
                                    </div>
                                </button>
                            ))}
                        </section>
                    );
                })}
            </aside>
        </div>
    );
};

const InfluioTv = () => {
    const [tab, setTab] = useState('live');

    return (
        <>
            <BreadcrumbsBar />
            <div className={styles.page}>
                <div className={`${styles.topbar} ${styles.rise}`}>
                    <div className={styles.wordmark}>
                        <span className={styles.liveDot} aria-hidden />
                        <div>
                            <div className={styles.brand}>Influio <span>TV</span></div>
                            <div className={styles.tagline}>Lia · günlük satış bülteni</div>
                        </div>
                    </div>
                    <div className={styles.tabs} role="tablist">
                        <button role="tab" aria-selected={tab === 'live'} className={`${styles.tab} ${tab === 'live' ? styles.on : ''}`} onClick={() => setTab('live')}>
                            <LiveTvOutlined fontSize="small" /> Yayın
                        </button>
                        <button role="tab" aria-selected={tab === 'studio'} className={`${styles.tab} ${tab === 'studio' ? styles.on : ''}`} onClick={() => setTab('studio')}>
                            <VideocamOutlined fontSize="small" /> Stüdyo
                        </button>
                    </div>
                </div>
                {tab === 'live' ? <Broadcast /> : <div className={`${styles.workbench} ${styles.rise} ${styles.d1}`}><InfluioTvStudio /></div>}
            </div>
        </>
    );
};

export default InfluioTv;
